import { Icon, token } from "@may-i/mash-design-system";
import * as RadixSlider from "@radix-ui/react-slider";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export const SCALE_MIN = 0.1;
export const SCALE_MAX = 3;
export const SCALE_STEP = 0.1;

const clampScale = (v: number) => Math.min(SCALE_MAX, Math.max(SCALE_MIN, Number(v.toFixed(1))));

/** 팝오버를 여는 도구 버튼용 아이콘 — 카메라 매핑 · 분석 초안 설정 공용 */
export function ResizeCameraIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M2.5 8.33398C7.56249 8.33398 11.6668 12.4376 11.667 17.5C11.667 17.9602 11.2932 18.334 10.833 18.334H2.5C2.03976 18.334 1.66699 17.9602 1.66699 17.5V9.16699C1.66701 8.70677 2.03977 8.33398 2.5 8.33398ZM17.5 9.16699C17.9601 9.16699 18.3328 9.53993 18.333 10V15.833C18.333 17.2137 17.2137 18.333 15.833 18.333H13.75C13.2898 18.333 12.917 17.9602 12.917 17.5C12.9172 17.0399 13.2899 16.667 13.75 16.667H15.833C16.2932 16.667 16.667 16.2932 16.667 15.833V10C16.6672 9.53993 17.0399 9.16699 17.5 9.16699ZM3.33301 16.667H9.95312C9.56877 13.1908 6.80923 10.4322 3.33301 10.0479V16.667ZM17.5 1.66699C17.9601 1.66699 18.3327 2.03999 18.333 2.5V5.83301C18.333 6.29321 17.9602 6.66699 17.5 6.66699C17.04 6.66676 16.667 6.29306 16.667 5.83301V4.5127L11.3662 9.8125C11.0408 10.1379 10.5129 10.1378 10.1875 9.8125C9.86237 9.48705 9.86219 8.95913 10.1875 8.63379L15.4883 3.33398L14.167 3.33301C13.7071 3.33271 13.3331 2.95997 13.333 2.5C13.3331 2.04005 13.7071 1.66729 14.167 1.66699H17.5ZM10 1.66699C10.4601 1.66699 10.8328 2.03993 10.833 2.5C10.833 2.96024 10.4602 3.33301 10 3.33301H4.16699C3.70687 3.33301 3.3332 3.70692 3.33301 4.16699V6.25C3.33301 6.71024 2.96024 7.08301 2.5 7.08301C2.03976 7.08301 1.66699 6.71024 1.66699 6.25V4.16699C1.66718 2.78644 2.7864 1.66699 4.16699 1.66699H10Z" fill="currentColor"/>
    </svg>
  );
}

interface ScaleSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  /** 드래그가 끝난(값이 확정된) 시점 — 저장은 여기서만. onChange 는 드래그 내내 발생 */
  onCommit: (value: number) => void;
}

function ScaleSlider({ label, value, onChange, onCommit }: ScaleSliderProps) {
  const step = (delta: number) => {
    const next = clampScale(value + delta);
    onChange(next);
    onCommit(next);
  };

  return (
    <>
      <ScaleRow>
        <ScaleLabel>{label}</ScaleLabel>
        <ScaleValue>{value.toFixed(1)}×</ScaleValue>
      </ScaleRow>
      <SliderRow>
        <SliderIconButton
          onClick={() => step(-SCALE_STEP)}
          disabled={value <= SCALE_MIN}
          aria-label={`${label} 축소`}
        >
          <Icon icon="ic_minus" width={14} />
        </SliderIconButton>
        {/* Radix — 드래그 중엔 onValueChange(로컬 상태만), 손 뗄 때 onValueCommit(저장).
            키보드 조작·확정도 Radix 가 같은 규약으로 처리한다. */}
        <SliderRoot
          min={SCALE_MIN}
          max={SCALE_MAX}
          step={SCALE_STEP}
          value={[value]}
          onValueChange={([v]) => onChange(v)}
          onValueCommit={([v]) => onCommit(v)}
        >
          <SliderTrack>
            <SliderRange />
          </SliderTrack>
          <SliderThumb aria-label={label} />
        </SliderRoot>
        <SliderIconButton
          onClick={() => step(SCALE_STEP)}
          disabled={value >= SCALE_MAX}
          aria-label={`${label} 확대`}
        >
          <Icon icon="ic_add" width={14} />
        </SliderIconButton>
      </SliderRow>
    </>
  );
}

interface CameraScalePopoverProps {
  iconScale: number;
  onIconScaleChange: (value: number) => void;
  onIconScaleCommit: (value: number) => void;
  labelScale: number;
  onLabelScaleChange: (value: number) => void;
  onLabelScaleCommit: (value: number) => void;
  onClose: () => void;
}

/**
 * 도면 위 카메라 아이콘 / 번호 라벨 크기 조절 팝오버.
 * 카메라 매핑 · 분석 초안 설정 두 화면이 공유 — 각 화면은 여는 버튼만 자기 도구모음에 둔다.
 */
export function CameraScalePopover({
  iconScale,
  onIconScaleChange,
  onIconScaleCommit,
  labelScale,
  onLabelScaleChange,
  onLabelScaleCommit,
  onClose,
}: CameraScalePopoverProps) {
  const { t } = useTranslation("camera-mapping");
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!popoverRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [onClose]);

  return (
    <Popover ref={popoverRef}>
      <ScaleTitle>{t("canvas.cameraScaleTitle")}</ScaleTitle>
      <ScaleSlider
        label={t("canvas.cameraIconScaleLabel")}
        value={iconScale}
        onChange={onIconScaleChange}
        onCommit={onIconScaleCommit}
      />
      <ScaleSlider
        label={t("canvas.cameraLabelScaleLabel")}
        value={labelScale}
        onChange={onLabelScaleChange}
        onCommit={onLabelScaleCommit}
      />
    </Popover>
  );
}

const Popover = styled.div`
  position: absolute;
  bottom: 172px;
  right: 12px;
  z-index: 10;
  width: 305px;
  padding: 8px 12px 12px;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  box-shadow:
    -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
`;

const ScaleTitle = styled.span`
  display: block;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
`;

const ScaleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 12px;
`;

const ScaleLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;

const ScaleValue = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  font-variant-numeric: tabular-nums;
  color: ${token("colors.text.disabled")};
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const SliderRoot = styled(RadixSlider.Root)`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  height: 16px;
  touch-action: none;
  user-select: none;
  cursor: pointer;
`;

const SliderTrack = styled(RadixSlider.Track)`
  position: relative;
  flex-grow: 1;
  height: 4px;
  border-radius: 100px;
  background: ${token("colors.gray.GR400")};
`;

const SliderRange = styled(RadixSlider.Range)`
  position: absolute;
  height: 100%;
  border-radius: 100px;
  background: ${token("colors.brand.primary")};
`;

const SliderThumb = styled(RadixSlider.Thumb)`
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid ${token("colors.brand.primary")};
  background: #fff;
  box-shadow: 0 1px 2px rgba(105, 111, 140, 0.24);
  transition: box-shadow 120ms ease;
  cursor: grab;

  &:hover {
    box-shadow: 0 0 0 4px ${token("colors.transparent.MB500.16")};
  }

  /* 키보드 포커스 링 — 접근성 (마우스 클릭엔 안 뜨도록 focus-visible) */
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px ${token("colors.transparent.MB500.32")};
  }

  &[data-disabled] {
    cursor: not-allowed;
  }
`;

const SliderIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${token("colors.text.primary")};

  &:disabled {
    cursor: default;
    color: ${token("colors.text.disabled")};
  }
`;
